import { useRef, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { Box, Heading, useDisclosure } from '@chakra-ui/react'
import {
  DragDropContext,
  Droppable,
  OnDragEndResponder,
} from '@hello-pangea/dnd'

import {
  useInterview,
  useUpdateInterview,
  useUpdateSummaryParts,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { StarRating } from '@/views/components/StarRating/StarRating'

import { AddCustomSummaryPart } from './AddCustomSummaryPart'
import { CustomPromptsLibrary, PromptTemplate } from './CustomPromptsLibrary'
import { DecisionSelector } from './DecisionSelector'
import { EmptySummary } from './EmptySummary'
import { ScoresSummaryBlock } from './ScoresSummaryBlock'
import { SummaryPartCard } from './SummaryPartCard'
import { Toolbox } from './Toolbox'

export const Summary = () => {
  const summaryRef = useRef<HTMLDivElement>(null)

  const interview = useInterview().data

  const summaryParts = (interview?.SummaryParts || []).sort(
    (a, b) => a.order - b.order,
  )

  const reportQuality = interview?.reportQuality
  const [isEditingParts, setIsEditingParts] = useState<boolean[]>(
    Array(summaryParts.length),
  )
  const [isAddCustomSummaryPartShown, setIsAddCustomSummaryPartShown] =
    useState<boolean>(false)
  const [promptTemplate, setPromptTemplate] = useState<PromptTemplate>()

  const customizeDisclosure = useDisclosure()

  const isViewOnlyUser = useIsViewOnlyMember()

  const updateInterview = useUpdateInterview()
  const updateSummaryParts = useUpdateSummaryParts()

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination || !interview?.SummaryParts) {
      return
    }

    const newItems = Array.from(interview.SummaryParts)
    newItems.sort((a, b) => a.order - b.order)

    let counter = -1
    let sourceIdx = 0
    let destinationIdx = 0
    for (let i = 0; i < newItems.length; ++i) {
      if (newItems[i].isSelected) {
        ++counter
      }

      if (counter === result.source.index) {
        sourceIdx = i
        break
      }
    }
    counter = -1
    for (let i = 0; i < newItems.length; ++i) {
      if (newItems[i].isSelected) {
        ++counter
      }

      if (counter === result.destination.index) {
        destinationIdx = i
        break
      }
    }

    if (sourceIdx < destinationIdx) {
      for (let i = sourceIdx + 1; i <= destinationIdx; ++i) {
        newItems[i].order--
      }
      newItems[sourceIdx].order = destinationIdx
    } else {
      for (let i = destinationIdx; i < sourceIdx; ++i) {
        newItems[i].order++
      }
      newItems[sourceIdx].order = destinationIdx
    }

    updateSummaryParts.mutate({
      summaryParts: newItems,
    })
  }

  const isIntake = interview?.type === 'INTAKE'
  const hasSummary = summaryParts.length > 0

  return (
    <div className="flex flex-col flex-grow-[2]">
      <div ref={summaryRef} className="flex flex-col">
        {!hasSummary && <EmptySummary />}
        {!isViewOnlyUser && hasSummary && (
          <Toolbox
            isCopyDisabled={isEditingParts.some(Boolean)}
            copyRef={summaryRef}
            isAddCustomSummaryPartShown={isAddCustomSummaryPartShown}
            setIsAddCustomSummaryPartShown={setIsAddCustomSummaryPartShown}
            customizeDisclosure={customizeDisclosure}
          />
        )}
        <ScoresSummaryBlock summaryParts={summaryParts} />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="report">
            {(provided) => (
              <AnimatePresence mode={'sync'}>
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {(summaryParts || [])
                    .sort((a, b) => a.order - b.order)
                    .map((part, initialIndex) => {
                      return {
                        part: part,
                        initialIndex,
                      }
                    })
                    .filter((data) => data.part.isSelected)
                    .map((data, idx) => {
                      return (
                        <SummaryPartCard
                          isAnimated={customizeDisclosure.isOpen}
                          key={data.part.id}
                          initialIndex={data.initialIndex}
                          idx={idx}
                          part={data.part}
                          isEditingParts={isEditingParts}
                          setIsEditingParts={setIsEditingParts}
                        />
                      )
                    })}

                  {provided.placeholder}
                </div>
              </AnimatePresence>
            )}
          </Droppable>
        </DragDropContext>
        <AnimatePresence mode="wait">
          {isAddCustomSummaryPartShown ? (
            <motion.div
              key="add-custom-summary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <AddCustomSummaryPart
                setIsAddCustomSummaryPartShown={setIsAddCustomSummaryPartShown}
                promptTemplate={promptTemplate}
              />
            </motion.div>
          ) : (
            !isViewOnlyUser && (
              <motion.div
                key="prompts-library"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <CustomPromptsLibrary
                  setIsAddCustomSummaryPartShown={
                    setIsAddCustomSummaryPartShown
                  }
                  setPromptTemplate={setPromptTemplate}
                />
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>

      <Box mt={10}>
        {!isIntake && (
          <div className="flex flex-row items-center gap-3 flex-wrap">
            <Heading display={'inline'} fontSize={'1rem'}>
              Your recommendation:
            </Heading>

            <DecisionSelector />
            <div className="flex-grow"></div>
          </div>
        )}
        <StarRating
          mt={10}
          ratingTypeText={'scorecard'}
          ratingValue={reportQuality}
          setRatingValue={(reportQuality: number) => {
            updateInterview.mutate({
              reportQuality,
            })
          }}
        />
      </Box>
    </div>
  )
}
